import { useState } from 'react';

import { useAuth } from '@/auth/AuthContext';
import { useChangeOwnPassword } from '@/hooks/useUsers';
import { ApiError } from '@/api/client';
import { ErrorBanner } from '@/components/common/States';
import { fmtDuration } from '@/lib/format';

const ROLE_LABEL: Record<string, string> = {
  system_admin: 'System admin',
  org_admin: 'Company admin',
  member: 'Member',
};

export function Account() {
  const { user } = useAuth();
  const changePw = useChangeOwnPassword();
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  async function submit() {
    setErr(null);
    setOk(false);
    if (next.length < 8) return setErr('New password must be at least 8 characters.');
    if (next !== confirm) return setErr('New password and confirmation do not match.');
    try {
      await changePw.mutateAsync({ current_password: cur, new_password: next });
      setOk(true);
      setCur(''); setNext(''); setConfirm('');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to change password.');
    }
  }

  return (
    <div className="fade-up" style={{ maxWidth: 560 }}>
      <div className="page-head">
        <div className="eyebrow">account</div>
        <div className="title">Your account</div>
        <div className="sub">Profile and password.</div>
      </div>

      <div className="card" style={{ marginBottom: 18, display: 'grid', gap: 8 }}>
        <div className="row spread"><span className="muted">Email</span><span className="mono">{user?.email}</span></div>
        <div className="row spread"><span className="muted">Role</span><span className="mono">{ROLE_LABEL[user?.role ?? ''] ?? user?.role}</span></div>
        <div className="row spread"><span className="muted">Balance</span><span className="mono signal">{fmtDuration(user?.traffic_seconds_balance)}</span></div>
      </div>

      <div className="card" style={{ display: 'grid', gap: 14 }}>
        <div className="eyebrow">change password</div>
        {err && <ErrorBanner error={err} />}
        {ok && <div className="mono" style={{ color: 'var(--signal)', fontSize: 13 }}>✓ Password changed. Other sessions were signed out.</div>}
        <div className="field">
          <label className="label">Current password</label>
          <input className="input" type="password" value={cur} onChange={(e) => setCur(e.target.value)} />
        </div>
        <div className="field">
          <label className="label">New password (min 8 chars)</label>
          <input className="input" type="password" value={next} onChange={(e) => setNext(e.target.value)} />
        </div>
        <div className="field">
          <label className="label">Confirm new password</label>
          <input className="input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        <div>
          <button className="btn btn-primary" onClick={submit} disabled={changePw.isPending || !cur || !next}>
            {changePw.isPending ? <span className="spinner" /> : 'Change password'}
          </button>
        </div>
      </div>
    </div>
  );
}
